import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  TableContainer,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../utils/axiosConfig';

interface ApplicationSubmission {
  _id: string;
  formType: string;
  formTitle: string;
  applicantName: string;
  applicantEmail: string;
  submittedAt: string;
  status: string;
  locationPreferences?: string;
}

type Order = 'asc' | 'desc';
type OrderBy = 'applicantName' | 'submittedAt' | 'status' | 'formType';

const ApplicationsList: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('submittedAt');
  const [selectedFormType, setSelectedFormType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchApplications();
  }, [selectedFormType, selectedStatus]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedFormType !== 'all') params.formType = selectedFormType;
      if (selectedStatus !== 'all') params.status = selectedStatus;

      const response = await axiosInstance.get<ApplicationSubmission[]>(
        '/api/applications/submissions',
        { params }
      );
      setApplications(response.data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching applications.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleStatusChange = async (applicationId: string, newStatus: string) => {
    try {
      await axiosInstance.patch(`/api/applications/submissions/${applicationId}/status`, {
        status: newStatus
      });
      setApplications(applications.map(app =>
        app._id === applicationId ? { ...app, status: newStatus } : app
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteClick = (applicationId: string) => {
    setApplicationToDelete(applicationId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (applicationToDelete) {
      try {
        await axiosInstance.delete(`/api/applications/submissions/${applicationToDelete}`);
        setApplications(applications.filter(app => app._id !== applicationToDelete));
      } catch (error) {
        console.error('Error deleting application:', error);
      }
    }
    setDeleteDialogOpen(false);
    setApplicationToDelete(null);
  };

  const sortApplications = (a: ApplicationSubmission, b: ApplicationSubmission) => {
    let aValue: any = a[orderBy];
    let bValue: any = b[orderBy];

    if (orderBy === 'submittedAt') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (aValue < bValue) {
      return order === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  };

  const getUniqueFormTypes = () => {
    const types = new Set(applications.map(app => app.formType));
    return Array.from(types);
  };

  const getPendingCount = () => {
    return applications.filter(app => app.status === 'pending').length;
  };

  const getApplicationFrequency = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    let todayCount = 0;
    let yesterdayCount = 0;
    let thisWeekCount = 0;
    let lastWeekCount = 0;

    applications.forEach(app => {
      const submittedDate = new Date(app.submittedAt);
      const submittedDay = new Date(submittedDate.getFullYear(), submittedDate.getMonth(), submittedDate.getDate());
      
      if (submittedDay.getTime() === today.getTime()) {
        todayCount++;
      } else if (submittedDay.getTime() === yesterday.getTime()) {
        yesterdayCount++;
      }
      
      if (submittedDate >= weekAgo) {
        thisWeekCount++;
      } else {
        const twoWeeksAgo = new Date(weekAgo);
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 7);
        if (submittedDate >= twoWeeksAgo) {
          lastWeekCount++;
        }
      }
    });

    return { todayCount, yesterdayCount, thisWeekCount, lastWeekCount };
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  const sortedApplications = [...applications].sort(sortApplications);
  const pendingCount = getPendingCount();
  const frequency = getApplicationFrequency();
  const isNavigatorSelected = selectedFormType.includes('navigator');

  return (
    <div>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          All Applications
        </Typography>
        
        {/* Pending Count Summary */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#1e2a38', borderRadius: 2 }}>
          <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
            {pendingCount} Pending
          </Typography>
        </Box>

        {/* Frequency Stats */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="textSecondary">Today</Typography>
            <Typography variant="h5">{frequency.todayCount}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="textSecondary">Yesterday</Typography>
            <Typography variant="h5">{frequency.yesterdayCount}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="textSecondary">This Week</Typography>
            <Typography variant="h5">{frequency.thisWeekCount}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="textSecondary">Last Week</Typography>
            <Typography variant="h5">{frequency.lastWeekCount}</Typography>
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Form Type</InputLabel>
            <Select
              value={selectedFormType}
              onChange={(e) => setSelectedFormType(e.target.value)}
              label="Filter by Form Type"
            >
              <MenuItem value="all">All Form Types</MenuItem>
              {getUniqueFormTypes().map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" color="textSecondary">
          Showing {sortedApplications.length} of {applications.length} applications
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'applicantName'}
                  direction={orderBy === 'applicantName' ? order : 'asc'}
                  onClick={() => handleRequestSort('applicantName')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'formType'}
                  direction={orderBy === 'formType' ? order : 'asc'}
                  onClick={() => handleRequestSort('formType')}
                >
                  Form Type
                </TableSortLabel>
              </TableCell>
              {isNavigatorSelected && (
                <TableCell>Location Preferences</TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'submittedAt'}
                  direction={orderBy === 'submittedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('submittedAt')}
                >
                  Submitted On
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedApplications.map((application) => (
              <TableRow key={application._id}>
                <TableCell>{application.applicantName}</TableCell>
                <TableCell>{application.applicantEmail}</TableCell>
                <TableCell>{application.formTitle}</TableCell>
                {isNavigatorSelected && (
                  <TableCell>
                    {application.locationPreferences || 'Not specified'}
                  </TableCell>
                )}
                <TableCell>
                  <Select
                    value={application.status}
                    onChange={(e) => handleStatusChange(application._id, e.target.value)}
                    size="small"
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  {new Date(application.submittedAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      component={Link}
                      to={`/admin/applications/${application._id}`}
                      variant="contained"
                      color="primary"
                      size="small"
                    >
                      View
                    </Button>
                    <IconButton
                      onClick={() => handleDeleteClick(application._id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this application? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ApplicationsList;